import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { usePageContentId } from '@confluence/page-context';
import { createSingleQueryParamHook } from '@confluence/route-manager';

import type { AdfStats } from './useAdfStats';

const STATSIG_DYNAMIC_CONFIG = 'cc_complexit_page_id_allowlist';

type UseProgressiveRenderingConfigResponseType = {
	isComplexPage: boolean;
	batchSize: number;
};

type UseProgressiveRenderingConfigProps = {
	totalNodeCount: AdfStats['totalNodeCount'];
};

const DEFAULT_COMPLEX_THREHOLD = 10000;
const DEFAULT_BATCH_SIZE = 1000;
const DEFAULT_MULTIPLIER = 10;

// complexitNodes value example: conplexitNodes=1000,10 where 1000 is the batch size and 10 is the multiplier
const useQueryParamComplexitNodes = createSingleQueryParamHook('complexitNodes');

export const useProgressiveRenderingConfig = ({
	totalNodeCount,
}: UseProgressiveRenderingConfigProps): UseProgressiveRenderingConfigResponseType => {
	const [contentId] = usePageContentId();

	const complexitNodesConfig = useQueryParamComplexitNodes()?.split(',');
	// Support partial value format like ,10 or 1000,
	const complexitBatchSize = Number(complexitNodesConfig?.[0] || DEFAULT_BATCH_SIZE);
	const complexitNodes =
		complexitBatchSize * Number(complexitNodesConfig?.[1] || DEFAULT_MULTIPLIER);
	const complexityThreshold = isNaN(complexitNodes) ? DEFAULT_COMPLEX_THREHOLD : complexitNodes;

	const pageIdAllowList = StatsigConfigurations.getStringList(STATSIG_DYNAMIC_CONFIG, []);

	return {
		isComplexPage:
			fg('cc_complexit_fe_progressive_rendering_condition') &&
			((!!contentId && pageIdAllowList.includes(contentId)) ||
				totalNodeCount >= complexityThreshold),
		batchSize: complexitBatchSize,
	};
};
